import { Gem, Info, LoaderCircle } from "lucide-react";

import { walletSatSourceIsUsable } from "../../lib/rarity";
import type { OrdinalSource, WalletSatSource } from "../../lib/types";

const numberFormatter = new Intl.NumberFormat("en-GB");

function sats(value: number): string {
  return `${numberFormatter.format(Math.max(0, Math.round(value)))} sats`;
}

function shortOutpoint(value: string): string {
  return value.length > 20 ? `${value.slice(0, 9)}…${value.slice(-7)}` : value;
}

export interface RareSatPickerProps {
  walletConnected: boolean;
  sources: WalletSatSource[];
  selectedOutpoints: string[];
  search: string;
  loading: boolean;
  enriching: boolean;
  loaded: boolean;
  error: string;
  selectionLimit: number;
  onLoad: () => void;
  onSearch: (value: string) => void;
  onToggle: (source: WalletSatSource) => void;
  onClear: () => void;
}

function SatComposition({ source }: { source: WalletSatSource }) {
  return <span className="sat-composition">
    <span className="sat-composition-heading">UTXO CONTENTS · SELECTED SAT OFFSET {numberFormatter.format(source.satOffset ?? 0)}</span>
    <span className="sat-composition-counts">
      {source.attributes.map((attribute) => <span key={attribute.slug}><strong>{attribute.label}</strong> ×{numberFormatter.format(attribute.count)}</span>)}
      {source.specialSatCoverage === "partial" ? <span>Other special-sat categories: unknown</span> : null}
    </span>
  </span>;
}

export function RareSatPicker({
  walletConnected,
  sources,
  selectedOutpoints,
  search,
  loading,
  enriching,
  loaded,
  error,
  selectionLimit,
  onLoad,
  onSearch,
  onToggle,
  onClear,
}: RareSatPickerProps) {
  const usableCount = sources.filter(walletSatSourceIsUsable).length;
  const normalizedSearch = search.trim().toLowerCase();
  const visible = sources
    .filter((source) => !normalizedSearch || `${source.sat} ${source.outpoint} ${source.rarity} ${source.primaryLabel} ${source.attributes.map((attribute) => attribute.label).join(" ")}`.toLowerCase().includes(normalizedSearch))
    .slice(0, 500);

  return <div className="single-sat-picker full" data-testid="rare-sat-picker">
    <div className="rare-sat-heading">
      <h3>RARE SAT SELECTION</h3>
      <div className="rare-sat-actions">
        {selectedOutpoints.length ? <button className="text-button" type="button" onClick={onClear}>CLEAR</button> : null}
        <button className="secondary-button compact" type="button" disabled={loading || enriching} onClick={onLoad}>
          {loading || enriching ? <LoaderCircle className="spin" /> : <Gem />}
          {!walletConnected ? "CONNECT WALLET" : loading ? "LOADING SATS" : enriching ? "ADDING DETAILS" : loaded ? "REFRESH" : "FIND IN WALLET"}
        </button>
      </div>
    </div>
    {sources.length ? <>
      <p className="rare-sat-empty">Partial special-sat index: known Magisat classifications are saved by STC. Unclassified sats are unknown, not confirmed common. New details load in the background; refresh to check again. Transaction safety checks still apply.</p>
      <div className="rare-sat-summary"><span>{selectedOutpoints.length} SELECTED</span><span>{usableCount} USABLE OUTPUTS</span></div>
      <input className="sat-search" value={search} onChange={(event) => onSearch(event.target.value)} placeholder="SEARCH SAT NUMBER OR UTXO" aria-label="Search wallet sats" />
    </> : null}
    {loading && !sources.length ? <p className="rare-sat-empty"><LoaderCircle className="spin" /> CHECKING TAPROOT WALLET…</p> : null}
    <div className="rare-sat-list">
      {visible.map((source) => {
        const selectedIndex = selectedOutpoints.indexOf(source.outpoint);
        const selected = selectedIndex >= 0;
        const usable = walletSatSourceIsUsable(source);
        return <button
          className={selected ? "rare-sat-row selected" : "rare-sat-row"}
          type="button"
          key={source.outpoint}
          aria-pressed={selected}
          disabled={!usable || (!selected && selectedOutpoints.length >= selectionLimit)}
          onClick={() => onToggle(source)}
        >
          <span className="rare-select-index">{!usable ? "×" : selected ? String(selectedIndex + 1).padStart(2, "0") : "+"}</span>
          <span><strong>{numberFormatter.format(source.sat)}</strong><small title={source.outpoint}>{shortOutpoint(source.outpoint)}</small></span>
          <span className={`rarity-badge ${source.primarySlug === "unknown" ? "common" : source.primarySlug !== source.rarity ? "special" : source.rarity}`}>{source.primaryLabel}</span>
          <span className="rare-sat-value">{usable ? sats(source.value) : "NOT FIRST SAT"}</span>
          <SatComposition source={source} />
        </button>;
      })}
    </div>
    {loaded && !loading && sources.length === 0 ? <p className="rare-sat-empty">NO CLEAN RARE-SAT OUTPUTS FOUND</p> : null}
    {!loading && sources.length > 0 && visible.length === 0 ? <p className="rare-sat-empty">NO MATCHES</p> : null}
    {error ? <div className="notice error" role="alert"><Info size={17} /><span>{error}</span></div> : null}
  </div>;
}

export function RareSatReview({ source }: { source?: OrdinalSource }) {
  if (!source) return <span className="review-sat-badge standard">STANDARD SAT</span>;
  const label = source.primaryLabel ?? source.rarity ?? "Selected sat";
  const specific = source.selectionKind === "specific";
  return <div className="review-sat-card" data-testid="rare-sat-review">
    <div>
      <span className="review-sat-badge special">{specific ? "SPECIFIC SAT" : `RARE SAT · ${label.toUpperCase()}`}</span>
      {source.sat !== undefined ? <strong>SAT {numberFormatter.format(source.sat)}</strong> : null}
    </div>
    <small>RARITY {label.toUpperCase()} · OFFSET {numberFormatter.format(source.satOffset ?? 0)}{specific && source.inscriptionId ? " · REINSCRIPTION" : ""}</small>
    <code>{source.outpoint}</code>
    {specific && source.inscriptionId ? <code>EXISTING · {source.inscriptionId}{source.parentRelation ? " · ORD PARENT LINK" : " · NO PARENT LINK"}</code> : null}
    {source.attributes?.length ? <span className="review-sat-contents">
      {source.attributes.map((attribute) => <em key={attribute.slug}>{attribute.label} ×{numberFormatter.format(attribute.count)}</em>)}
    </span> : null}
  </div>;
}
